import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import posthog from "posthog-js";
import { Card, Button, Image, Modal, Form, TextArea, Dropdown, Icon, Segment, Grid, Header, Divider, Statistic, Container } from "semantic-ui-react";
import { Badge } from "../../common";
import moment from "moment";
import * as leaveApprovalActions from "./leaveApprovalActions";
import { getUserInfo } from "../../../api/axios";
import { Common } from "../../../utils";
import { isSubscriptionSuspended, fetchSubscriptionData } from "../../../utils/subscriptionUtils";
import SubscriptionRestriction from "../../common/subscriptionRestriction/SubscriptionRestriction";
import "./LeaveApprovalDashboard.css";


class LeaveApprovalDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedApproval: null,
            showApprovalModal: false,
            approvalAction: null,
            comments: "",
            activeTab: "pending",
            filterStatus: "0", // Default to pending status
            searchText: "",
            isSubscriptionSuspended: false,
            showTextModal: false,
            textModalTitle: "",
            textModalContent: "",
        };
    }

    async componentDidMount() {
        // Check subscription status
        try {
            const subscriptionData = await fetchSubscriptionData();
            if (subscriptionData && isSubscriptionSuspended(subscriptionData)) {
                this.setState({ isSubscriptionSuspended: true });
                return; // Don't load approvals if subscription is restricted
            }
        } catch (error) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("leaveApprovalDashboardSubscription", {
                    type: "exception",
                    success: false,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    status: error?.response?.status,
                });
            }
        }

        this.props.leaveApprovalActions.getApprovalStats();
        this.props.leaveApprovalActions.getLeaveRequest(0, 1, 12, false); // Load pending approvals by default
    }

    componentWillUnmount() {
        // Clear search timeout to prevent memory leaks
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    }

    handleApprove = (approval) => {
        this.setState({
            selectedApproval: approval,
            approvalAction: "approve",
            showApprovalModal: true,
            comments: "",
        });
    };

    handleReject = (approval) => {
        this.setState({
            selectedApproval: approval,
            approvalAction: "reject",
            showApprovalModal: true,
            comments: "",
        });
    };

    handleSubmitApproval = () => {
        const { selectedApproval, approvalAction, comments } = this.state;

        const approvalData = {
            groupId: selectedApproval.group, // Use group instead of approvalId
            comments: comments.trim() || "", // Allow empty comments
            status: approvalAction === "approve" ? 1 : 2, // 1=Approved, 2=Rejected
        };

        this.props.leaveApprovalActions.updateLeaveStatus(approvalData, this.state.filterStatus).then(() => {
            this.setState({
                showApprovalModal: false,
                selectedApproval: null,
                approvalAction: null,
                comments: "",
            });
        });
    };

    handleCloseModal = () => {
        this.setState({
            showApprovalModal: false,
            selectedApproval: null,
            approvalAction: null,
            comments: "",
        });
    };

    handleShowFullText = (title, content) => {
        this.setState({
            showTextModal: true,
            textModalTitle: title,
            textModalContent: content || "",
        });
    };

    handleCloseTextModal = () => {
        this.setState({
            showTextModal: false,
            textModalTitle: "",
            textModalContent: "",
        });
    };

    handleLoadMore = () => {
        const { leaveApprovalReducer } = this.props;
        const { pagination } = leaveApprovalReducer;
        const { activeTab } = this.state;

        if (pagination.hasMore && !leaveApprovalReducer.loadingMore) {
            const nextPage = pagination.currentPage + 1;
            const status = activeTab === "all" ? null :
                activeTab === "pending" ? 0 :
                    activeTab === "approved" ? 1 :
                        activeTab === "rejected" ? 2 : null;
            this.props.leaveApprovalActions.getLeaveRequest(status, nextPage, pagination.pageSize, true);
        }
    };

    handleTabChange = (newTab) => {
        this.setState({ activeTab: newTab });
        const status = newTab === "all" ? null :
            newTab === "pending" ? 0 :
                newTab === "approved" ? 1 :
                    newTab === "rejected" ? 2 : null;

        // If there's a search query, use search API, otherwise use regular getLeaveRequest
        if (this.state.searchText && this.state.searchText.trim()) {
            this.performSearch(status);
        } else {
            this.props.leaveApprovalActions.getLeaveRequest(status, 1, 12, false);
        }
    };

    performSearch = (status = null) => {
        const { searchText } = this.state;
        const { activeTab } = this.state;

        if (!searchText || !searchText.trim()) {
            // If no search text, fall back to regular getLeaveRequest
            const tabStatus = activeTab === "all" ? null :
                activeTab === "pending" ? 0 :
                    activeTab === "approved" ? 1 :
                        activeTab === "rejected" ? 2 : null;
            this.props.leaveApprovalActions.getLeaveRequest(tabStatus, 1, 12, false);
            return;
        }

        const searchParams = {
            query: searchText.trim(),
            approvalStatus: status,
            page: 1,
            take: 12,
            sortBy: "createdDate",
            sortDirection: "desc"
        };

        this.props.leaveApprovalActions.searchLeaveApprovals(searchParams);
    };

    handleSearchChange = (e) => {
        const searchText = e.target.value;
        this.setState({ searchText });

        // Debounce search - wait 500ms after user stops typing
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.performSearch();
        }, 500);
    };

    clearSearch = () => {
        this.setState({ searchText: "" });
        // Clear any pending search timeout
        clearTimeout(this.searchTimeout);
        // Reset to regular view based on current tab
        const { activeTab } = this.state;
        const status = activeTab === "all" ? null :
            activeTab === "pending" ? 0 :
                activeTab === "approved" ? 1 :
                    activeTab === "rejected" ? 2 : null;
        this.props.leaveApprovalActions.getLeaveRequest(status, 1, 12, false);
    };


    getDefaultAvatar = (name) => {
        if (!name) return this.getGenericAvatar();

        // Get initials from name
        const initials = name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 2);

        // Generate a consistent color based on name
        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
            '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
        ];
        const colorIndex = name.charCodeAt(0) % colors.length;
        const backgroundColor = colors[colorIndex];

        // Create SVG avatar
        const svg = `
            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="20" fill="${backgroundColor}"/>
                <text x="20" y="26" font-family="Arial, sans-serif" font-size="14" font-weight="bold" 
                      text-anchor="middle" fill="white">${initials}</text>
            </svg>
        `;

        return `data:image/svg+xml;base64,${btoa(svg)}`;
    };

    getGenericAvatar = () => {
        const svg = `
            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="20" fill="#E0E0E0"/>
                <circle cx="20" cy="16" r="6" fill="#9E9E9E"/>
                <path d="M8 32c0-6.627 5.373-12 12-12s12 5.373 12 12" fill="#9E9E9E"/>
            </svg>
        `;
        return `data:image/svg+xml;base64,${btoa(svg)}`;
    };

    getHalfDayPeriod = (startingLeave, endingLeave) => {
        // Handle different enum values for half day periods
        if (startingLeave === 0 || startingLeave === 'Morning' || startingLeave === 'AM') {
            return '(Morning)';
        } else if (startingLeave === 1 || startingLeave === 'Afternoon' || startingLeave === 'PM') {
            return '(Afternoon)';
        } else if (endingLeave === 0 || endingLeave === 'Morning' || endingLeave === 'AM') {
            return '(Morning)';
        } else if (endingLeave === 1 || endingLeave === 'Afternoon' || endingLeave === 'PM') {
            return '(Afternoon)';
        } else if (startingLeave === 2 || startingLeave === 'Evening' || startingLeave === 'Evening') {
            return '(Evening)';
        } else if (endingLeave === 2 || endingLeave === 'Evening' || endingLeave === 'Evening') {
            return '(Evening)';
        }

        // Default fallback
        return '(Half Day)';
    };

    getStatusBadge = (status) => {
        const statusConfig = {
            0: { color: "yellow", text: "Pending", icon: "clock" },
            1: { color: "green", text: "Approved", icon: "check circle" },
            2: { color: "red", text: "Rejected", icon: "times circle" },
        };

        const config = statusConfig[status] || statusConfig[0];
        return (
            <Badge color={config.color} size="small" icon={config.icon}>
                {config.text}
            </Badge>
        );
    };

    getFilteredApprovals = () => {
        const { leaveRequest } = this.props.leaveApprovalReducer;
        const { activeTab } = this.state;

        // Since we're using API search, we only need to filter by status if not using search
        // The API handles the search filtering, so we just return the data as-is
        let filteredData = leaveRequest;

        // Only apply client-side status filtering if we're not using search
        if (!this.state.searchText || !this.state.searchText.trim()) {
            if (activeTab === "pending") {
                filteredData = filteredData.filter(approval => approval.approvalStatus === 0);
            } else if (activeTab === "approved") {
                filteredData = filteredData.filter(approval => approval.approvalStatus === 1);
            } else if (activeTab === "rejected") {
                filteredData = filteredData.filter(approval => approval.approvalStatus === 2);
            }
        }

        return filteredData;
    };

    renderApprovalCard = (approval) => {
        const startDate = moment(approval.startDate).format("MMM DD, YYYY");
        const endDate = moment(approval.endDate).format("MMM DD, YYYY");
        const isMultiDay = approval.isMultiDayRequest || startDate !== endDate;

        return (
            <Card key={approval.leaveId} className="approval-card">
                <Card.Content>
                    <div className="approval-header">
                        <div className="employee-info">
                            <div className="employee-avatar-wrap">
                                <Image
                                    src={approval.profilePicture || this.getDefaultAvatar(approval.name)}
                                    size="mini"
                                    circular
                                    className="employee-avatar"
                                    onError={(e) => {
                                        const defaultAvatar = this.getDefaultAvatar(approval.name);
                                        if (e.target.src !== defaultAvatar) {
                                            e.target.src = defaultAvatar;
                                        }
                                    }}
                                />
                                <div className="employee-avatar-zoom" aria-hidden="true">
                                    <img
                                        src={approval.profilePicture || this.getDefaultAvatar(approval.name)}
                                        alt=""
                                        onError={(e) => {
                                            const defaultAvatar = this.getDefaultAvatar(approval.name);
                                            if (e.target.src !== defaultAvatar) {
                                                e.target.src = defaultAvatar;
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <div>
                                <Header as="h4" className="employee-name">
                                    {approval.name}
                                </Header>
                                <p className="employee-details">
                                    {approval.designation && `${approval.designation}`}
                                    {approval.designation && approval.departmentName && ' '}
                                    {approval.departmentName && `${approval.departmentName}`}
                                </p>
                                {approval.officeName && (
                                    <p className="employee-office">
                                        <Icon name="building" size="small" />
                                        {approval.officeName}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="approval-status">
                            {this.getStatusBadge(approval.approvalStatus)}
                        </div>
                    </div>

                    <Divider />

                    <div className="leave-details">
                        <div className="leave-type" style={{
                            color: approval.leaveTypeColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Icon name="calendar" />
                                <strong>{approval.leaveType}</strong>
                            </div>
                            {approval.leaveDays > 0 && (
                                <span className="leave-days" style={{
                                    color: '#3b82f6',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                }}>
                                    ({approval.leaveDays === 0.5 ?
                                        `0.5 day ${this.getHalfDayPeriod(approval.startingLeave, approval.endingLeave)}` :
                                        approval.leaveDays === 1 ? '1 day' :
                                            `${approval.leaveDays} days`})
                                </span>
                            )}
                        </div>
                        <div className="leave-dates">
                            <Icon name="clock" />
                            <span>{isMultiDay ? `${startDate} - ${endDate}` : startDate}</span>
                        </div>
                        <div className="leave-note" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px',
                            minHeight: '24px'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                flex: 1,
                                overflow: 'hidden'
                            }}>
                                <Icon name="sticky note" />
                                <span
                                    title={approval.leaveNote && approval.leaveNote.length > 50 ? approval.leaveNote : undefined}
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1
                                    }}
                                >
                                    {approval.leaveNote && approval.leaveNote.trim()
                                        ? (approval.leaveNote.length > 50
                                            ? `${approval.leaveNote.substring(0, 50)}...`
                                            : approval.leaveNote)
                                        : '-'
                                    }
                                </span>
                            </div>
                            {approval.leaveNote && approval.leaveNote.length > 50 && (
                                <Button
                                    icon="expand"
                                    size="mini"
                                    compact
                                    basic
                                    onClick={() => this.handleShowFullText("Leave Note", approval.leaveNote)}
                                    style={{
                                        padding: '4px 8px',
                                        fontSize: '11px',
                                        flexShrink: 0
                                    }}
                                >
                                    View Full
                                </Button>
                            )}
                        </div>
                    </div>

                    {approval.approvalStatus === 0 && (
                        <div className="approval-actions">
                            <Button
                                size="small"
                                icon="times"
                                content={this.props.t("app.leaveApproval.reject")}
                                className="btn-reject-action"
                                onClick={() => this.handleReject(approval)}
                                loading={this.props.leaveApprovalReducer.loading}
                            />
                            <Button
                                size="small"
                                icon="check"
                                content={this.props.t("app.leaveApproval.approve")}
                                className="btn-approve-action"
                                onClick={() => this.handleApprove(approval)}
                                loading={this.props.leaveApprovalReducer.loading}
                            />
                        </div>
                    )}

                    <div className="approval-meta">
                        <small>
                            <Icon name="user" />
                            {this.props.t("app.leaveApproval.approver")}: {approval.approverName || this.props.t("app.leaveApproval.notAssigned")}
                        </small>
                        <small>
                            <Icon name="calendar outline" />
                            Requested: {moment(approval.requestDate).format("MMM DD, YYYY")}
                        </small>
                        {approval.approvalDate && (
                            <small>
                                <Icon name="check circle" />
                                {this.props.t("app.leaveApproval.approvedOn")}: {moment(approval.approvalDate).format("MMM DD, YYYY")}
                            </small>
                        )}
                    </div>

                    {approval.approvalStatus !== 0 && (
                        <>
                            <Divider style={{ margin: '12px 0' }} />
                            <div className="approval-comment-section" style={{
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                borderLeft: `3px solid ${approval.approvalStatus === 1 ? '#0d9668' : '#c53030'}`
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '8px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <Icon
                                            name="comment"
                                            size="small"
                                            color={approval.approvalStatus === 1 ? 'green' : 'red'}
                                        />
                                        <strong style={{
                                            fontSize: '13px',
                                            color: '#374151',
                                            fontWeight: '600'
                                        }}>
                                            {this.props.t("app.leaveApproval.approverComment")}:
                                        </strong>
                                    </div>
                                    {approval.approvalComment && approval.approvalComment.trim() && approval.approvalComment.length > 50 && (
                                        <Button
                                            icon="expand"
                                            size="mini"
                                            compact
                                            basic
                                            onClick={() => this.handleShowFullText(this.props.t("app.leaveApproval.approverComment"), approval.approvalComment)}
                                            style={{
                                                padding: '4px 8px',
                                                fontSize: '11px',
                                                flexShrink: 0
                                            }}
                                        >
                                            View Full
                                        </Button>
                                    )}
                                </div>
                                <p style={{
                                    margin: 0,
                                    fontSize: '13px',
                                    color: '#4b5563',
                                    lineHeight: '1.5',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {approval.approvalComment && approval.approvalComment.trim()
                                        ? (approval.approvalComment.length > 50
                                            ? `${approval.approvalComment.substring(0, 50)}...`
                                            : approval.approvalComment)
                                        : '-'
                                    }
                                </p>
                            </div>
                        </>
                    )}
                </Card.Content>
            </Card>
        );
    };

    render() {
        const { leaveApprovalReducer } = this.props;
        const { loading, loadingMore, error, approvalStats, pagination } = leaveApprovalReducer;
        const { activeTab, filterStatus, searchText, showApprovalModal, selectedApproval, approvalAction, comments, isSubscriptionSuspended, showTextModal, textModalTitle, textModalContent } = this.state;

        // If subscription is suspended, show restriction message
        if (isSubscriptionSuspended) {
            return (
                <div style={{ padding: "20px" }}>
                    <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessApprovalsPage" />
                </div>
            );
        }

        const filteredLeaveRequest = this.getFilteredApprovals();


        return (
            <Container className="py-4 px-3 px-md-4" fluid>
                <div className="leave-approval-dashboard">
                    {/* Professional Header Section */}
                    <div className="lapproval-header-bar">
                        <div className="lapproval-header-content">
                            <div className="lapproval-header-left">
                                <div className="lapproval-header-icon-wrapper">
                                    <Icon name="check circle" style={{ margin: 0, fontSize: '20px' }} />
                                </div>
                                <div className="lapproval-header-text">
                                    <h1 className="lapproval-page-title">
                                        {this.props.t("app.leaveApproval.title")}
                                    </h1>
                                    <p className="lapproval-page-description">
                                        {this.props.t("app.leaveApproval.description")}
                                    </p>
                                </div>
                            </div>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => this.props.history.push("/dashboard")}
                                style={{
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    fontWeight: '500',
                                    borderWidth: '2px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <i className="bi bi-arrow-left me-2"></i>{this.props.t("app.leaveApproval.backToDashboard")}
                            </button>
                        </div>
                    </div>

                    {/* Statistics */}
                    <Grid columns={4} stackable className="statistics-grid" style={{ marginBottom: '30px' }}>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #fbbf24',
                                borderTop: '4px solid #f59e0b',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#f59e0b', marginBottom: '8px' }}>
                                    {approvalStats?.pendingApprovals ?? 0}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    {this.props.t("app.leaveApproval.pendingApprovals")}
                                </div>
                            </div>
                        </Grid.Column>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #10b981',
                                borderTop: '4px solid #059669',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#059669', marginBottom: '8px' }}>
                                    {approvalStats?.approvedRequests ?? 0}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    {this.props.t("app.leaveApproval.approved")}
                                </div>
                            </div>
                        </Grid.Column>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #ef4444',
                                borderTop: '4px solid #dc2626',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#dc2626', marginBottom: '8px' }}>
                                    {approvalStats?.rejectedRequests ?? 0}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    {this.props.t("app.leaveApproval.rejected")}
                                </div>
                            </div>
                        </Grid.Column>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #3b82f6',
                                borderTop: '4px solid #2563eb',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#2563eb', marginBottom: '8px' }}>
                                    {approvalStats?.totalRequests ?? 0}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    {this.props.t("app.leaveApproval.totalRequests")}
                                </div>
                            </div>
                        </Grid.Column>
                    </Grid>

                    {/* Filters and Tabs */}
                    <Segment className="filters-section" style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        marginBottom: '20px',
                        padding: '20px 24px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '20px',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <Button.Group style={{
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexWrap: 'nowrap'
                                }}>
                                    <Button
                                        active={activeTab === "pending"}
                                        onClick={() => this.handleTabChange("pending")}
                                        style={{
                                            backgroundColor: activeTab === "pending" ? '#fef3c7' : '#ffffff',
                                            color: activeTab === "pending" ? '#f59e0b' : '#6b7280',
                                            border: activeTab === "pending" ? '2px solid #f59e0b' : '2px solid #e5e7eb',
                                            fontWeight: '600',
                                            transition: 'all 0.3s ease',
                                            borderRadius: activeTab === "pending" ? '8px 0 0 8px' : '0',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <Icon name="clock" style={{ marginRight: '6px' }} />
                                        {this.props.t("app.leaveApproval.pending")} ({approvalStats?.pendingApprovals ?? 0})
                                    </Button>
                                    <Button
                                        active={activeTab === "approved"}
                                        onClick={() => this.handleTabChange("approved")}
                                        style={{
                                            backgroundColor: activeTab === "approved" ? '#d1fae5' : '#ffffff',
                                            color: activeTab === "approved" ? '#059669' : '#6b7280',
                                            border: activeTab === "approved" ? '2px solid #059669' : '2px solid #e5e7eb',
                                            fontWeight: '600',
                                            transition: 'all 0.3s ease',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <Icon name="check circle" style={{ marginRight: '6px' }} />
                                        {this.props.t("app.leaveApproval.approved")} ({approvalStats?.approvedRequests ?? 0})
                                    </Button>
                                    <Button
                                        active={activeTab === "rejected"}
                                        onClick={() => this.handleTabChange("rejected")}
                                        style={{
                                            backgroundColor: activeTab === "rejected" ? '#fee2e2' : '#ffffff',
                                            color: activeTab === "rejected" ? '#dc2626' : '#6b7280',
                                            border: activeTab === "rejected" ? '2px solid #dc2626' : '2px solid #e5e7eb',
                                            fontWeight: '600',
                                            transition: 'all 0.3s ease',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <Icon name="times circle" style={{ marginRight: '6px' }} />
                                        {this.props.t("app.leaveApproval.rejected")} ({approvalStats?.rejectedRequests ?? 0})
                                    </Button>
                                    <Button
                                        active={activeTab === "all"}
                                        onClick={() => this.handleTabChange("all")}
                                        style={{
                                            backgroundColor: activeTab === "all" ? '#dbeafe' : '#ffffff',
                                            color: activeTab === "all" ? '#2563eb' : '#6b7280',
                                            border: activeTab === "all" ? '2px solid #2563eb' : '2px solid #e5e7eb',
                                            fontWeight: '600',
                                            transition: 'all 0.3s ease',
                                            borderRadius: '0 8px 8px 0',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <Icon name="list" style={{ marginRight: '6px' }} />
                                        {this.props.t("app.leaveApproval.allStatus")} ({approvalStats?.totalRequests ?? 0})
                                    </Button>
                                </Button.Group>
                            </div>

                            <div style={{
                                position: 'relative',
                                flex: '1',
                                minWidth: '300px',
                                maxWidth: '500px'
                            }}>
                                <Form.Input
                                    placeholder={this.props.t("app.leaveApproval.searchPlaceholder")}
                                    value={searchText}
                                    onChange={this.handleSearchChange}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        backgroundColor: '#f9fafb',
                                        fontSize: '14px',
                                        padding: '10px 40px 10px 15px',
                                        fontWeight: '400',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#667eea';
                                        e.target.style.backgroundColor = '#ffffff';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.backgroundColor = '#f9fafb';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                                {searchText && searchText.trim() && (
                                    <Button
                                        icon="close"
                                        circular
                                        size="mini"
                                        onClick={this.clearSearch}
                                        style={{
                                            position: 'absolute',
                                            right: '8px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            backgroundColor: 'rgba(0,0,0,0.1)',
                                            color: '#666',
                                            border: 'none',
                                            width: '20px',
                                            height: '20px',
                                            minHeight: '20px',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = 'rgba(0,0,0,0.2)';
                                            e.target.style.color = '#333';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'rgba(0,0,0,0.1)';
                                            e.target.style.color = '#666';
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </Segment>

                    {/* Approvals List */}
                    {loading ? (
                        <Segment loading>
                            <div style={{ height: "200px" }}></div>
                        </Segment>
                    ) : error ? (
                        <Segment color="red">
                            <Header as="h4" color="red">
                                <Icon name="warning" />
                                Error
                            </Header>
                            <p>{error}</p>
                        </Segment>
                    ) : filteredLeaveRequest.length === 0 ? (
                        <Segment textAlign="center" className="empty-state">
                            <Icon name="calendar check outline" size="huge" color="grey" />
                            <Header as="h3" color="grey">
                                No approvals found
                            </Header>
                            <p>There are no leave approvals matching your current filters.</p>
                        </Segment>
                    ) : (
                        <div>
                            <Card.Group className="approvals-grid">
                                {filteredLeaveRequest.map(this.renderApprovalCard)}
                            </Card.Group>

                            {/* Load More Button */}
                            {pagination.hasMore && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <Button
                                        primary
                                        loading={loadingMore}
                                        onClick={this.handleLoadMore}
                                        disabled={loadingMore}
                                    >
                                        {loadingMore ? 'Loading...' : 'Load More'}
                                    </Button>
                                    <p style={{ marginTop: '10px', color: '#666' }}>
                                        Showing {filteredLeaveRequest.length} of {pagination.totalItems} approvals
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Approval Modal */}
                    <Modal open={showApprovalModal} onClose={this.handleCloseModal} size="small" className="lapproval-modal">
                        <Modal.Header className="lapproval-modal-header-gradient">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icon name={approvalAction === "approve" ? "check circle" : "times circle"} />
                                <span>{approvalAction === "approve" ? this.props.t("app.leaveApproval.approveLeaveRequest") : this.props.t("app.leaveApproval.rejectLeaveRequest")}</span>
                            </div>
                        </Modal.Header>
                        <Modal.Content>
                            {selectedApproval && (
                                <div className="modal-details-grid">
                                    <div className="modal-detail-item">
                                        <span className="label">Employee:</span>
                                        <span className="value">{selectedApproval.name}</span>
                                    </div>
                                    <div className="modal-detail-item">
                                        <span className="label">Department:</span>
                                        <span className="value">{selectedApproval.departmentName}</span>
                                    </div>
                                    <div className="modal-detail-item">
                                        <span className="label">Leave Type:</span>
                                        <span className="value" style={{ color: selectedApproval.leaveTypeColor, fontWeight: '700' }}>
                                            {selectedApproval.leaveType}
                                        </span>
                                    </div>
                                    <div className="modal-detail-item">
                                        <span className="label">Date:</span>
                                        <span className="value">
                                            {moment(selectedApproval.startDate).format("MMM DD, YYYY")}
                                            {selectedApproval.isMultiDayRequest && ` - ${moment(selectedApproval.endDate).format("MMM DD, YYYY")}`}
                                            {selectedApproval.leaveDays === 0.5 && (
                                                <span className="half-day-tag">
                                                    {this.getHalfDayPeriod(selectedApproval.startingLeave, selectedApproval.endingLeave)}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    {selectedApproval.leaveNote && (
                                        <div className="modal-detail-item full-width">
                                            <span className="label">Leave Note:</span>
                                            <span className="value note-text">{selectedApproval.leaveNote}</span>
                                        </div>
                                    )}
                                    <div className="modal-detail-item full-width">
                                        <Form style={{ width: '100%', marginTop: '10px' }}>
                                            <Form.Field>
                                                <label className="modal-form-label">{this.props.t("app.leaveApproval.commentsOptional")}</label>
                                                <TextArea
                                                    placeholder={this.props.t("app.leaveApproval.commentsPlaceholderOptional")}
                                                    value={comments}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value.length <= 1000) {
                                                            this.setState({ comments: value });
                                                        }
                                                    }}
                                                    rows={3}
                                                    className="modal-textarea"
                                                    maxLength={1000}
                                                />
                                                <div style={{
                                                    textAlign: 'right',
                                                    marginTop: '5px',
                                                    fontSize: '12px',
                                                    color: comments.length >= 1000 ? '#dc2626' : '#6b7280'
                                                }}>
                                                    {comments.length} / 1000 characters
                                                </div>
                                            </Form.Field>
                                        </Form>
                                    </div>
                                </div>
                            )}
                        </Modal.Content>
                        <Modal.Actions className="modal-actions-container">
                            <Button
                                onClick={this.handleCloseModal}
                                className="btn-cancel"
                            >
<Icon name="cancel" /> {this.props.t("app.common.cancel")}
                                </Button>
                                <Button
                                onClick={this.handleSubmitApproval}
                                loading={loading}
                                className={approvalAction === "approve" ? "btn-approve" : "btn-reject"}
                            >
                                <Icon name={approvalAction === "approve" ? "check" : "times"} />
                                {approvalAction === "approve" ? this.props.t("app.leaveApproval.approve") : this.props.t("app.leaveApproval.reject")}
                            </Button>
                        </Modal.Actions>
                    </Modal>

                    {/* Text Viewer Modal */}
                    <Modal
                        open={showTextModal}
                        onClose={this.handleCloseTextModal}
                        size="small"
                        className="lapproval-modal"
                    >
                        <Modal.Header className="lapproval-modal-header-gradient">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icon name="file text outline" />
                                <span>{textModalTitle}</span>
                            </div>
                        </Modal.Header>
                        <Modal.Content scrolling>
                            <div style={{
                                padding: '16px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                minHeight: '100px',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>
                                <p style={{
                                    margin: 0,
                                    fontSize: '14px',
                                    color: '#374151',
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {textModalContent}
                                </p>
                            </div>
                        </Modal.Content>
                        <Modal.Actions className="modal-actions-container">
                            <Button
                                onClick={this.handleCloseTextModal}
                                className="btn-cancel"
                            >
<Icon name="cancel" /> {this.props.t("app.common.cancel")}
                                </Button>
                        </Modal.Actions>
                    </Modal>
                </div>
            </Container>
        );
    }
}

const mapStateToProps = (state) => ({
    leaveApprovalReducer: state.leaveApprovalReducer,
});

const mapDispatchToProps = (dispatch) => ({
    leaveApprovalActions: bindActionCreators(leaveApprovalActions, dispatch),
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(LeaveApprovalDashboard));